/**
 * How a fact's value is written on an office's sheet, or in its text copy.
 *
 * One formatter per template `format` (text | date | money | number | percent |
 * months | yes_no). On the sheet, a "$" or "%" the sheet already prints beside
 * the blank (`prefix_printed` / `suffix_printed`) is not written again; the text
 * copy always carries its own symbols. Null in, null out: an unknown value is
 * never written as anything.
 */
import dayjs from "dayjs";

function toNumber(value) {
  if (typeof value === "number") return isFinite(value) ? value : null;
  const n = Number(String(value).replace(/[$,%\s]/g, ""));
  return String(value).trim() !== "" && isFinite(n) ? n : null;
}

/** 80 → "80", 12.5 → "12.5", 33.333 → "33.33". */
export function trimNumber(n) {
  return String(Math.round(n * 100) / 100);
}

/** 1000 → "1,000", 1000.5 → "1,000.50". */
export function money(n) {
  const cents = Math.round(n * 100) / 100;
  const whole = Math.trunc(cents) === cents;
  const [int, frac] = Math.abs(cents).toFixed(whole ? 0 : 2).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cents < 0 ? "-" : ""}${grouped}${frac ? `.${frac}` : ""}`;
}

/** "calendar_year" → "Calendar year"; "yes" → "Yes". Other text is left alone. */
export function humanize(value) {
  const s = String(value);
  if (s === "yes") return "Yes";
  if (s === "no") return "No";
  if (/^[a-z]+(_[a-z]+)+$/.test(s)) {
    const words = s.replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return s;
}

function prints(printed, symbol) {
  return String(printed || "").indexOf(symbol) >= 0;
}

/**
 * The text for one blank, or null when there is nothing to write.
 * `field`: {format, prefix_printed, suffix_printed}. `text`: for the text copy.
 */
export function formatValue(value, field, { text = false } = {}) {
  if (value === null || value === undefined || value === "") return null;
  const f = field || {};
  const printedBefore = text ? "" : f.prefix_printed;
  const printedAfter = text ? "" : f.suffix_printed;
  const sheetPrints = (symbol) => prints(printedBefore, symbol) || prints(printedAfter, symbol);
  const n = toNumber(value);

  switch (f.format) {
    case "money":
      if (n === null) return String(value);
      return sheetPrints("$") ? money(n) : `$${money(n)}`;
    case "percent":
      if (n === null) return String(value);
      return sheetPrints("%") ? trimNumber(n) : `${trimNumber(n)}%`;
    case "number":
      return n === null ? String(value) : trimNumber(n);
    case "months":
      if (n === null) return String(value);
      return /month|mo\b/i.test(String(printedAfter || "")) ? trimNumber(n)
        : `${trimNumber(n)} ${n === 1 ? "month" : "months"}`;
    case "date": {
      const raw = String(value);
      const d = /^\d{4}-\d{2}-\d{2}/.test(raw) ? dayjs(raw.slice(0, 10)) : null;
      return d && d.isValid() ? d.format("MMM D, YYYY") : raw;
    }
    case "yes_no":
      return humanize(value);
    default:
      return typeof value === "number" ? trimNumber(value) : humanize(value);
  }
}

/** The format a fact of `type` is written in when nothing else says (an
 * option group's value that matches none of its options). */
export function formatForType(type) {
  return {
    money: "money", percent: "percent", number: "number", months: "months", date: "date", yes_no: "yes_no",
  }[type] || "text";
}
